// API functions for category tree management.
// Covers all four category domains: control, event, action, framework.
// A single API module parameterised by type string.

import { z } from 'zod'
import type { ApiClient } from './client'

// --- Types ---

/** Valid category type strings matching backend DomainConfig keys. */
export type CategoryType = 'control' | 'event' | 'action' | 'framework'

export const CATEGORY_TYPES: CategoryType[] = ['control', 'event', 'action', 'framework']

/** Category labels for display (page titles, breadcrumbs). */
export const categoryTypeLabels: Record<CategoryType, string> = {
  control: 'Control',
  event: 'Event',
  action: 'Action',
  framework: 'Framework',
}

/** Plural labels for object counts. */
export const categoryObjectLabels: Record<CategoryType, string> = {
  control: 'controls',
  event: 'events',
  action: 'actions',
  framework: 'compliance frameworks',
}

export interface Category {
  id: string
  parent_id: string | null
  path: string
  depth: number
  name: string
  description?: string | null
  owner_role_id: string
  owner_role_label: string
  owner_user_id?: string | null
  owner_display_name?: string | null
  object_count: number
  child_count: number
  has_children: boolean
  created_at?: string
  updated_at?: string
}

export interface CreateCategoryRequest {
  parent_id: string
  name: string
  description?: string
  owner_role_id: string
}

export interface UpdateCategoryRequest {
  name?: string
  description?: string
  owner_role_id?: string
}

// --- Tree node helper (client-side hierarchy from flat list) ---

export interface CategoryTreeNode extends Category {
  children: CategoryTreeNode[]
}

/** Build a tree from the flat array returned by GET /categories/{type}/tree (ordered by path). */
export function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const nodeMap = new Map<string, CategoryTreeNode>()
  const roots: CategoryTreeNode[] = []

  for (const cat of categories) {
    nodeMap.set(cat.id, { ...cat, children: [] })
  }

  for (const cat of categories) {
    const node = nodeMap.get(cat.id)!
    if (cat.parent_id && nodeMap.has(cat.parent_id)) {
      nodeMap.get(cat.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}

// --- Zod validation schemas ---

export const createCategorySchema = z.object({
  parent_id: z.string().uuid('Select a parent category'),
  name: z.string().min(1, 'Name is required').max(200, 'Name must be 200 characters or less'),
  description: z.string().max(1000, 'Description must be 1000 characters or less').optional(),
  owner_role_id: z.string().uuid('Select an ownership role'),
})

export const updateCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Name cannot be empty')
    .max(200, 'Name must be 200 characters or less')
    .optional(),
  description: z.string().max(1000, 'Description must be 1000 characters or less').optional(),
  owner_role_id: z.string().uuid('Select an ownership role').optional(),
})

// --- API functions ---

export function categoriesApi(api: ApiClient, type: CategoryType) {
  const base = `/categories/${type}`
  return {
    tree: () => api.get<Category[]>(`${base}/tree`),
    list: (search?: string) => {
      const params = search ? `?search=${encodeURIComponent(search)}` : ''
      return api.get<Category[]>(`${base}${params}`)
    },
    get: (id: string) => api.get<Category>(`${base}/${id}`),
    create: (data: CreateCategoryRequest) => api.post<Category>(base, data),
    update: (id: string, data: UpdateCategoryRequest) =>
      api.put<Category>(`${base}/${id}`, data),
    delete: (id: string) => api.del(`${base}/${id}`),
    children: (id: string) => api.get<Category[]>(`${base}/${id}/children`),
  }
}
